import Config from "@app/constants/Config";
import { buildYup } from "schema-to-yup";
import { endOfDay, startOfDay, subDays } from "date-fns";

const toDate = (value) => {
	if (value === null || value === undefined || value === "") return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const DATE_BOUND_RESOLVERS = {
	today: () => startOfDay(new Date()),
	todayMinus28: () => subDays(startOfDay(new Date()), Config.CONFIRMATION_DATE_MAX_PAST_DAYS),
};

// test(context, value, params) — params are passed via closure at attach time,
// because yup v1 does not expose .test() options params on the test context
const customFieldTests = {
	dateRange: {
		defaultMessage: () => Config.ERROR_CONFIRMATION_DATE_RANGE_MESSAGE,
		test: (context, value, [minBound, maxBound]) => {
			const date = toDate(value);
			if (!date) return true;
			const min = DATE_BOUND_RESOLVERS[minBound]?.();
			const max = DATE_BOUND_RESOLVERS[maxBound]?.();
			if (min && startOfDay(date) < startOfDay(min)) return false;
			if (max && endOfDay(date) > endOfDay(max)) return false;
			return true;
		},
	},
	dateAfterField: {
		defaultMessage: () => Config.ERROR_FOLLOWUP_BEFORE_INSPECTION_MESSAGE,
		test: (context, value, [fieldName]) => {
			const date = toDate(value);
			const other = toDate(context.parent?.[fieldName]);
			if (!date || !other) return true;
			return startOfDay(date) >= startOfDay(other);
		},
	},
};

const getYupSchema = (fields, instantRequired = false) => {
	const schema = {
		type: "object",
		properties: {},
	};
	const errMessages = {};
	fields
		.filter(({ validation }) => {
			return !instantRequired || (instantRequired && validation.instantCheck);
		})
		// .filter(({ layout }) => !layout) // TODO why was this filter applied?
		.forEach(({ name, validation, elementType, useSingle, multiselect }) => {
			if (elementType === "attributeSelector" && useSingle) {
				elementType = "attributeSelectorSingle";
			}
			if (elementType === "select" && multiselect) {
				elementType = "selectMulti";
			}
			const type = Config.FIELD_TYPE_MAPPING[elementType] ?? "string";

			schema.properties[name] = {
				type: validation.type || type,
				required: validation.required,
			};
			errMessages[name] = {
				required:
					validation.validationErrorMessage !== ""
						? validation.validationErrorMessage
						: Config.ERROR_DEFAULT_MESSAGE,
			};
		});

	const yupSchema = buildYup(schema, { errMessages });

	fields
		.filter(({ validation }) => {
			return !instantRequired || (instantRequired && validation.instantCheck);
		})
		.forEach(({ name, validation }) => {
			const customTest = validation?.customTest;
			const definition = customFieldTests[customTest?.id];
			const fieldSchema = yupSchema.fields?.[name];
			if (!definition || !fieldSchema) return;
			const message = customTest.message || definition.defaultMessage();
			const params = customTest.params ?? [];
			yupSchema.fields[name] = fieldSchema.test({
				name: customTest.id,
				message,
				test: function (value) {
					return definition.test(this, value, params);
				},
			});
		});

	return yupSchema;
};

export default getYupSchema;
